import { useCallback, useState } from "react"

/**
 * Generic state hook for an editable list of items with a stable identity.
 *
 * Consolidates the near-identical add/remove pattern that was repeated across the
 * ship-data editor tabs: append a freshly built `{ id, ... }` item, or remove an
 * item by its index. The parent owns the "new item" template via `makeItem`, so
 * each collection differs only in that factory.
 *
 * @param initial - initial items (evaluated once, as the useState initializer)
 * @param makeItem - factory for a new item, invoked on `add`
 * @returns { items, setItems, add, removeAt }
 *
 * @example
 * const sails = useEditableList<IShipDataSail>(data.sails || [], () => ({ id: newId(), sail: "" }));
 * sails.items.map(...); sails.add(); sails.removeAt(index);
 */
export interface EditableList<T> {
  items: T[];
  setItems: React.Dispatch<React.SetStateAction<T[]>>;
  add: () => void;
  removeAt: (index: number) => void;
}

export function useEditableList<T>(initial: T[], makeItem: () => T): EditableList<T> {
  const [items, setItems] = useState<T[]>(initial);

  const add = useCallback(() => {
    setItems((prev) => [...prev, makeItem()]);
  }, [makeItem]);

  const removeAt = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  return { items, setItems, add, removeAt };
}
